/**
 * App theme — a 1:1 port of AppTheme in ios/Fuse/Fuse/FuseApp.swift.
 * Seven user-selectable looks (black, white, blue, green, pink, orange, red)
 * plus a color-intensity slider that softens colored themes toward pastel.
 * Persisted per-device in localStorage, same as iOS's @AppStorage.
 */

export type ThemeName = "black" | "white" | "blue" | "green" | "pink" | "orange" | "red";
export const THEME_NAMES: ThemeName[] = ["black", "white", "blue", "green", "pink", "orange", "red"];
export const DEFAULT_INTENSITY = 1.0;

type RGB = [number, number, number];

// Full-screen background colors (FuseApp.swift AppTheme.background).
const BACKGROUND: Record<ThemeName, RGB> = {
  black: [0, 0, 0],
  white: [255, 255, 255],
  blue: [15, 77, 224],
  green: [15, 140, 69],
  pink: [230, 31, 122],
  orange: [245, 107, 15],
  red: [224, 26, 33],
};

// Accents — iOS system colors (dark variants), per AppTheme.accent.
const ACCENT: Record<ThemeName, RGB> = {
  black: [255, 55, 95], // .pink
  white: [255, 45, 85], // .pink (light)
  pink: [255, 55, 95],
  blue: [10, 132, 255],
  green: [48, 209, 88],
  orange: [255, 159, 10],
  red: [255, 69, 58],
};

// Picker swatches (AppTheme.swatch).
export const SWATCH: Record<ThemeName, string> = {
  black: "#000000",
  white: "#ffffff",
  blue: "#1a6bff",
  green: "#1ab861",
  pink: "#ff73b8",
  orange: "#ff8c1a",
  red: "#e62929",
};

export const THEME_LABEL: Record<ThemeName, string> = {
  black: "Black", white: "White", blue: "Blue", green: "Green",
  pink: "Pink", orange: "Orange", red: "Red",
};

const isNeutral = (t: ThemeName) => t === "black" || t === "white";

// ---- color math (mirrors mutedColor in FuseApp.swift) ----
function rgbToHsb([r, g, b]: RGB): [number, number, number] {
  const rr = r / 255, gg = g / 255, bb = b / 255;
  const max = Math.max(rr, gg, bb), min = Math.min(rr, gg, bb);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rr) h = ((gg - bb) / d) % 6;
    else if (max === gg) h = (bb - rr) / d + 2;
    else h = (rr - gg) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, max === 0 ? 0 : d / max, max];
}

function hsbToRgb([h, s, v]: [number, number, number]): RGB {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rgb: [number, number, number];
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return [Math.round((rgb[0] + m) * 255), Math.round((rgb[1] + m) * 255), Math.round((rgb[2] + m) * 255)];
}

/** Soften a color toward muted/pastel as intensity drops 1 → 0. */
function muted(base: RGB, intensity: number, lighten = 0.4): RGB {
  const t = Math.max(0, Math.min(1, intensity));
  if (t >= 0.999) return base;
  const m = 1 - t;
  const [h, s, b] = rgbToHsb(base);
  return hsbToRgb([h, s * (1 - 0.75 * m), b + (1 - b) * (lighten * m)]);
}

const css = ([r, g, b]: RGB) => `rgb(${r}, ${g}, ${b})`;

// ---- persistence (same keys/semantics as iOS @AppStorage) ----
export function loadTheme(): { theme: ThemeName; intensity: number } {
  const t = localStorage.getItem("appTheme") as ThemeName | null;
  const i = parseFloat(localStorage.getItem("themeIntensity") ?? "");
  return {
    theme: t && THEME_NAMES.includes(t) ? t : "black",
    intensity: Number.isFinite(i) ? Math.max(0.3, Math.min(1, i)) : DEFAULT_INTENSITY,
  };
}

export function saveTheme(theme: ThemeName, intensity: number): void {
  localStorage.setItem("appTheme", theme);
  localStorage.setItem("themeIntensity", String(intensity));
  applyTheme(theme, intensity);
}

/** Paint the theme as CSS variables on :root. */
export function applyTheme(theme: ThemeName, intensity: number): void {
  const root = document.documentElement;
  const light = theme === "white";
  const bg = isNeutral(theme) ? BACKGROUND[theme] : muted(BACKGROUND[theme], intensity, 0.4);
  const accent = muted(ACCENT[theme], intensity, 0);

  root.style.setProperty("--bg", css(bg));
  root.style.setProperty("--accent", css(accent));
  const [ar, ag, ab] = accent;
  root.style.setProperty("--accent-dim", `rgba(${ar}, ${ag}, ${ab}, 0.75)`);

  if (light) {
    // White theme = light mode (AppTheme.scheme).
    root.style.setProperty("--surface", "#f2f2f7");
    root.style.setProperty("--surface-2", "#e5e5ea");
    root.style.setProperty("--text", "#111114");
    root.style.setProperty("--text-dim", "#6e6e73");
    root.style.setProperty("--border", "#d9d9de");
    root.style.setProperty("--bar-bg", "rgba(255,255,255,0.72)");
    root.style.setProperty("--on-bg", "#111114");
    root.style.setProperty("--on-bg-dim", "#55555a");
  } else {
    // Dark cards float on the colored/black field (matches the iOS screenshots).
    root.style.setProperty("--surface", "rgba(16, 18, 17, 0.92)");
    root.style.setProperty("--surface-2", "rgba(255, 255, 255, 0.10)");
    root.style.setProperty("--text", "#f5f5f5");
    root.style.setProperty("--text-dim", "rgba(235, 235, 245, 0.62)");
    root.style.setProperty("--border", "rgba(255, 255, 255, 0.10)");
    root.style.setProperty("--bar-bg", "rgba(0, 0, 0, 0.28)");
    // Text drawn directly on the themed background (titles, section labels).
    root.style.setProperty("--on-bg", "#ffffff");
    root.style.setProperty("--on-bg-dim", "rgba(255, 255, 255, 0.72)");
  }

  // PWA status-bar color follows the theme.
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", css(bg));
}

export function initTheme(): void {
  const { theme, intensity } = loadTheme();
  applyTheme(theme, intensity);
}
